import { key, keyCodes } from './helpers';

/**
 * On-page jump navigation.
 *
 * Scroll-spy resolves by cached position rather than IntersectionObserver:
 * anchors are flat siblings, so targets are points inside sections that can be
 * thousands of pixels tall, and an observer fires nothing mid-section.
 */
const initJumpNav = (nav) => {
  const COLLAPSE_WIDTH = 1200;
  const SCROLL_SETTLE_MS = 1000;
  const links = Array.from(nav.querySelectorAll('[data-jump-nav-link]'));
  const current = nav.querySelector('[data-jump-nav-current]');
  const linkList = nav.querySelector('[data-jump-nav-links]');
  const toggle = nav.querySelector('[data-jump-nav-toggle]');
  const drawer = nav.querySelector('[data-jump-nav-drawer]');
  const progress = nav.querySelector('.jump-nav__progress');
  const targets = Array.from(document.querySelectorAll('[data-jump-nav-target]'));

  if (!targets.length) {
    return;
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  let offsets = [];
  let activeId = null;
  let ticking = false;
  let suppressUntil = 0;

  const stickyOffset = () => nav.getBoundingClientRect().bottom;

  const measure = () => {
    const { scrollY } = window;

    offsets = targets
      .map((el) => ({
        id: el.id,
        top: el.getBoundingClientRect().top + scrollY,
      }))
      .filter(
        (entry) =>
          entry.id && links.some((l) => l.dataset.jumpNavLink === entry.id),
      )
      .sort((a, b) => a.top - b.top);
  };

  const setActive = (id) => {
    if (id === activeId) {
      return;
    }
    activeId = id;

    links.forEach((link) => {
      if (link.dataset.jumpNavLink === id) {
        link.setAttribute('aria-current', 'location');
      } else {
        link.removeAttribute('aria-current');
      }
    });

    if (current) {
      const link = links.find((l) => l.dataset.jumpNavLink === id);
      current.textContent = link ? link.textContent.trim() : '';
    }
  };

  const resolve = () => {
    const { scrollY } = window;
    const threshold = scrollY + stickyOffset() + 1;
    const atBottom =
      window.innerHeight + scrollY >= document.documentElement.scrollHeight - 2;

    if (atBottom && offsets.length) {
      setActive(offsets[offsets.length - 1].id);
      return;
    }

    let found = null;
    offsets.forEach((entry) => {
      if (entry.top <= threshold) {
        found = entry.id;
      }
    });

    setActive(found);
  };

  // Stuck once the nav's top reaches its pinned sticky offset.
  const updateStuck = () => {
    const pinnedAt = parseFloat(getComputedStyle(nav).top) || 0;
    nav.classList.toggle('subnav--jump--stuck', nav.getBoundingClientRect().top <= pinnedAt + 1);
  };

  const updateProgress = () => {
    if (!progress) {
      return;
    }

    const main = document.querySelector('.main-content') || document.body;
    const top = main.getBoundingClientRect().top + window.scrollY;
    const travel = main.offsetHeight - window.innerHeight;
    const pct = travel > 0 ? (window.scrollY - top) / travel : 0;

    progress.style.setProperty(
      '--scroll-progress',
      `${Math.min(100, Math.max(0, pct * 100))}%`,
    );
  };

  const onScroll = () => {
    if (ticking) {
      return;
    }
    ticking = true;

    window.requestAnimationFrame(() => {
      ticking = false;
      updateProgress();
      updateStuck();

      if (Date.now() < suppressUntil) {
        return;
      }
      resolve();
    });
  };

  const subsiteNav = document.querySelector('.subnav--subsite');

  const closeDrawer = (returnFocus) => {
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
    }
    nav.classList.remove('subnav--jump--open');

    if (returnFocus && toggle) {
      toggle.focus();
    }
  };

  const closeSubsiteNav = () => {
    if (!subsiteNav || typeof bootstrap === 'undefined') {
      return;
    }

    const subsiteToggle = subsiteNav.querySelector(
      '.subnav__mobile-toggle[data-bs-target]',
    );
    const target =
      subsiteToggle &&
      document.querySelector(subsiteToggle.getAttribute('data-bs-target'));
    const instance = target && bootstrap.Collapse.getInstance(target);

    if (instance) {
      instance.hide();
    }
  };

  const updateCollapse = () => {
    if (!linkList) {
      return;
    }

    const narrow = window.innerWidth < COLLAPSE_WIDTH;
    let overflows = false;

    if (!narrow) {
      nav.classList.remove('subnav--jump--collapsed');
      overflows = linkList.scrollWidth > linkList.clientWidth + 1;
    }

    nav.classList.toggle('subnav--jump--collapsed', narrow || overflows);

    if (!(narrow || overflows)) {
      closeDrawer();
    }
  };

  links.forEach((link) => {
    link.addEventListener('click', () => {
      setActive(link.dataset.jumpNavLink);
      suppressUntil =
        Date.now() + (reducedMotion.matches ? 0 : SCROLL_SETTLE_MS);
      closeDrawer();
    });
  });

  if (toggle && drawer) {
    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      nav.classList.toggle('subnav--jump--open', !open);

      if (!open) {
        closeSubsiteNav();
      }
    });

    nav.addEventListener('keyup', (e) => {
      if (
        key(e) === keyCodes.esc &&
        nav.classList.contains('subnav--jump--open')
      ) {
        closeDrawer(true);
      }
    });
  }

  if (subsiteNav) {
    ['show.bs.collapse', 'show.bs.dropdown'].forEach((name) => {
      subsiteNav.addEventListener(name, () => closeDrawer());
    });
  }

  if ('onscrollend' in window) {
    window.addEventListener('scrollend', () => {
      suppressUntil = 0;
      resolve();
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });

  const ro = new ResizeObserver(() => {
    measure();
    updateCollapse();
    updateProgress();
    updateStuck();
    resolve();
  });
  ro.observe(document.documentElement);

  measure();
  updateCollapse();
  updateProgress();
  updateStuck();
  resolve();
};

Drupal.behaviors.ukhcJumpNav = {
  attach(context) {
    once('ukhc-jump-nav', '.subnav--jump', context).forEach(initJumpNav);
  },
};
